import { request } from '../../Common/api/client';

const ENDPOINT = '/api/admin/cp-settlement/payment-period';
const PAGE_SIZE = 100;
const STATUSES = new Set([
  'NOT_AGGREGATED', 'SETTLEMENT_PENDING', 'TRANSFER_PENDING', 'TRANSFERRING',
  'PARTIALLY_TRANSFERRED', 'TRANSFERRED', 'TRANSFER_FAILED', 'CONFIRMING',
  'NO_PAYOUT', 'REVIEW_REQUIRED',
]);

function monthIndex(value) {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(String(value ?? ''));
  return match ? Number(match[1]) * 12 + Number(match[2]) - 1 : null;
}

function positiveInteger(value) {
  const text = String(value ?? '');
  if (!/^\d+$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function nonnegativeInteger(value) {
  const text = String(value ?? '');
  if (!/^\d+$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
}

/** Preserve missing/invalid values as null; in particular, null is not 0. */
export function readPaymentPeriodAmount(value) {
  if (typeof value === 'string') {
    const text = value.trim();
    if (!/^-?\d+(?:\.\d+)?$/.test(text)) return null;
    value = Number(text);
  }
  return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER ? value : null;
}

function validateParameters({ monthFrom, monthTo, status, cpId }) {
  const from = monthIndex(monthFrom);
  const to = monthIndex(monthTo);
  if (from === null || to === null || to < from || to - from >= 12) {
    throw new Error('조회 기간은 시작 월부터 최대 12개월까지 선택해 주세요.');
  }
  const params = { monthFrom, monthTo, limit: PAGE_SIZE };
  if (cpId !== undefined && cpId !== null && cpId !== '') {
    const parsed = positiveInteger(cpId);
    if (parsed === null) throw new Error('가맹점 정보를 확인해 주세요.');
    params.cpId = parsed;
  }
  if (status !== undefined && status !== null && status !== '') {
    const values = (Array.isArray(status) ? status : String(status).split(',')).map((value) => String(value).trim());
    if (!values.length || values.some((value) => !STATUSES.has(value))) throw new Error('조회 상태를 확인해 주세요.');
    params.status = [...new Set(values)].join(',');
  }
  return params;
}

function abortIfNeeded(signal) {
  if (signal?.aborted) {
    const error = new Error('조회가 취소되었습니다.');
    error.name = 'AbortError';
    throw error;
  }
}

/**
 * Fetch all CP/PG/payment-day rows, using the server's whole-period summary
 * only once. A changing/invalid page is an error, not a partial report.
 */
export async function fetchPaymentPeriod({ monthFrom, monthTo, status, cpId, signal }) {
  const params = validateParameters({ monthFrom, monthTo, status, cpId });
  let firstSummary;
  let expectedTotal;
  let expectedLimit;
  let expectedPages;
  const rows = [];
  const rowKeys = new Set();

  for (let page = 1; expectedPages === undefined || page <= expectedPages; page += 1) {
    abortIfNeeded(signal);
    const data = await request({ method: 'GET', url: ENDPOINT, params: { ...params, page }, signal, timeout: 30000 });
    abortIfNeeded(signal);
    const pagination = data?.pagination;
    const total = nonnegativeInteger(pagination?.total);
    const returnedPage = positiveInteger(pagination?.page);
    const limit = positiveInteger(pagination?.limit);
    const rawPages = nonnegativeInteger(pagination?.totalPages);
    const totalPages = rawPages === 0 && total === 0 ? 1 : rawPages;
    if (!Array.isArray(data?.rows) || total === null || returnedPage !== page || limit === null || limit > PAGE_SIZE || totalPages === null || totalPages < 1 || totalPages !== Math.max(1, Math.ceil(total / limit))) {
      throw new Error('결제일별 정산의 페이지 정보를 확인할 수 없습니다. 다시 조회해 주세요.');
    }
    if (page === 1) {
      if (!data.summary || typeof data.summary !== 'object' || Array.isArray(data.summary)) {
        throw new Error('결제일별 정산의 전체 합계를 확인할 수 없습니다.');
      }
      firstSummary = data.summary;
      expectedTotal = total;
      expectedLimit = limit;
      expectedPages = totalPages;
    } else if (total !== expectedTotal || limit !== expectedLimit || totalPages !== expectedPages) {
      throw new Error('조회 중 정산 내역이 변경되었습니다. 다시 조회해 주세요.');
    }
    const expectedRows = Math.min(limit, Math.max(0, total - (page - 1) * limit));
    if (data.rows.length !== expectedRows) {
      throw new Error('결제일별 정산 목록이 완전하지 않습니다. 다시 조회해 주세요.');
    }
    for (const row of data.rows) {
      if (!row || typeof row !== 'object' || Array.isArray(row) || typeof row.rowKey !== 'string' || !row.rowKey.trim() || rowKeys.has(row.rowKey)) {
        throw new Error('결제일별 정산 내역이 중복되거나 올바르지 않습니다. 다시 조회해 주세요.');
      }
      rowKeys.add(row.rowKey);
      rows.push(row);
    }
  }
  return { rows, summary: firstSummary };
}
